import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { GAME_PAYMENT_MODE } from "../../../constants/game";
import { GameStep } from "../CreateGame/parts";
import {
  isDateTimeValid,
  isLocationValid,
  isPaymentValid,
  isPlayerDetailsValid,
  isSportVisibilityValid,
} from "../shared/gameFormValidation";

const EditGameForm = ({
  gameData,
  setGameData,
  originalGame,
  isLoading,
  handleGameInputChange,
  handleGameTypeChange,
  handleSportChange,
  handleLocationSelect,
  handleAddressChange,
  handleVenueChange,
  handlePlayerAmountChange,
  handleRevolutToggle,
  handleRevolutLinkChange,
  handleSubmitGame,
}) => {
  const navigate = useNavigate();
  const { gameId } = useParams();

  const [initialSnapshot] = useState(() => JSON.stringify(gameData));
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const hasUnsavedChanges = JSON.stringify(gameData) !== initialSnapshot;

  const isFormValid =
    isSportVisibilityValid(gameData) &&
    isDateTimeValid(gameData) &&
    isLocationValid(gameData) &&
    isPlayerDetailsValid(gameData) &&
    isPaymentValid(gameData);

  const hasActivePlayersOtherThanHost = (
    originalGame?.active_players || []
  ).some((playerId) => playerId !== originalGame?.created_by);
  const isPaymentLocked =
    originalGame?.payment_mode === GAME_PAYMENT_MODE.IN_APP &&
    hasActivePlayersOtherThanHost;

  const goToGameDetails = () => {
    navigate(`/game-details/${gameId}`);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      goToGameDetails();
    }
  };

  const handleSave = async (e) => {
    const result = await handleSubmitGame(e);
    if (result?.success) {
      goToGameDetails();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          p: { xs: 0, md: 3 },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Text variant="pageHeader">Edit game</Text>
        </Box>

        <GameStep
          gameData={gameData}
          onGameInputChange={handleGameInputChange}
          onGameTypeChange={handleGameTypeChange}
          onGameDataChange={setGameData}
          onSportChange={handleSportChange}
          onLocationSelect={handleLocationSelect}
          onAddressChange={handleAddressChange}
          onVenueChange={handleVenueChange}
          onPlayerAmountChange={handlePlayerAmountChange}
          onRevolutToggle={handleRevolutToggle}
          onRevolutLinkChange={handleRevolutLinkChange}
          paymentReadOnly={isPaymentLocked}
        />
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: { xs: "fixed", md: "static" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "column" },
          gap: 2,
          p: 2,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: { xs: "auto", md: "200px", lg: "272px" },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
        }}
      >
        <Button
          variant="primary"
          onClick={handleSave}
          sx={{ fontSize: "16px", py: 1.5 }}
          disabled={isLoading || !hasUnsavedChanges || !isFormValid}
        >
          {isLoading ? "Saving..." : "Save changes"}
        </Button>
        <Button
          variant="secondary"
          onClick={handleBack}
          sx={{ fontSize: "16px", py: 1.5 }}
        >
          Cancel
        </Button>
      </Box>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Unsaved changes
          </Text>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph">
            You have unsaved changes. Do you want to discard them?
          </Text>
        </DialogContent>
        <Box
          sx={{ p: 3, pt: 1, gap: 1, display: "flex", flexDirection: "column" }}
        >
          <Button
            sx={{ width: "auto" }}
            variant="primary"
            onClick={() => setShowUnsavedDialog(false)}
          >
            Continue editing
          </Button>
          <Button
            sx={{ width: "auto" }}
            variant="secondary"
            onClick={goToGameDetails}
          >
            Discard changes
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default EditGameForm;
